import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ChevronLeft, 
  Store as StoreIcon,
  Mail,
  Phone,
  MessageCircle,
  Loader,
  User as UserIcon,
  ExternalLink
} from 'lucide-react';
import { RecommendedProductsRow } from './RecommendedProductsRow'; // ← NEW

const API_URL = process.env.REACT_APP_API_URL;

function StoreInfo({ delivery, onBack }) {
  const [storeData, setStoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStoreData();
  }, [delivery.user_id]);

  const fetchStoreData = async () => {
    if (!delivery.user_id) {
      setError('No hay información de tienda disponible');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/user/${delivery.user_id}`);
      setStoreData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching store data:', err);
      setError('No se pudo cargar la información de la tienda');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = () => {
    if (storeData?.phone_number) {
      const message = encodeURIComponent(`Hola, soy ${delivery.recipient_name}. Tengo una consulta sobre mi pedido.`);
      const cleanPhone = storeData.phone_number.replace(/\D/g, '');
      window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
    }
  };

  const handleEmail = () => {
    if (storeData?.email) {
      window.location.href = `mailto:${storeData.email}?subject=Consulta sobre pedido&body=Hola, soy ${delivery.recipient_name}. Tengo una consulta sobre mi pedido.`;
    }
  };

  const handleCall = () => {
    if (storeData?.phone_number) {
      window.location.href = `tel:${storeData.phone_number}`;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-brand-gold to-brand-primary shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200 active:scale-95"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-lg shadow-md">
                <StoreIcon className="w-6 h-6 text-brand-gold" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-white font-montserrat">
                  Información Tienda
                </h1>
                <p className="text-xs text-white/90 font-montserrat">
                  Contacta al vendedor
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">

          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <Loader className="w-12 h-12 text-brand-primary animate-spin mx-auto mb-4" />
                <p className="text-slate-600 font-montserrat">Cargando información...</p>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
              <StoreIcon className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-red-800 font-montserrat font-semibold mb-2">
                Error al cargar información
              </p>
              <p className="text-sm text-red-600 font-montserrat">{error}</p>
            </div>
          )}

          {!loading && !error && storeData && (
            <>
              {/* Main Info Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
                <div className="bg-gradient-to-r from-brand-gold/10 to-brand-primary/10 p-6 border-b border-slate-200">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-brand-gold to-brand-primary flex items-center justify-center shadow-lg flex-shrink-0">
                      <UserIcon className="w-10 h-10 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-2xl font-extrabold text-brand-primary font-montserrat mb-1">
                        {storeData.username}
                      </h2>
                      <p className="text-sm text-slate-600 font-montserrat">Vendedor verificado</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  {storeData.email && (
                    <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-600 font-montserrat mb-1">Correo electrónico</p>
                        <p className="font-semibold text-slate-900 font-montserrat break-all">
                          {storeData.email}
                        </p>
                      </div>
                    </div>
                  )}

                  {storeData.phone_number && (
                    <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-600 font-montserrat mb-1">Teléfono</p>
                        <p className="font-semibold text-slate-900 font-montserrat">
                          {storeData.phone_number}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Buttons */}
              <div className="space-y-3 mb-6">
                <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-4">
                  ¿Necesitas contactar al vendedor?
                </h3>

                {storeData.phone_number && (
                  <button
                    onClick={handleWhatsApp}
                    className="w-full flex items-center gap-4 p-5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl hover:shadow-lg active:scale-95 transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-bold font-montserrat text-lg">Enviar WhatsApp</p>
                      <p className="text-sm text-white/90 font-montserrat">Chatea directamente con el vendedor</p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-white/80" />
                  </button>
                )}

                {storeData.phone_number && (
                  <button
                    onClick={handleCall}
                    className="w-full flex items-center gap-4 p-5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl hover:shadow-lg active:scale-95 transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-bold font-montserrat text-lg">Llamar ahora</p>
                      <p className="text-sm text-white/90 font-montserrat">Habla directamente con el vendedor</p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-white/80" />
                  </button>
                )}

                {storeData.email && (
                  <button
                    onClick={handleEmail}
                    className="w-full flex items-center gap-4 p-5 bg-gradient-to-r from-brand-primary to-brand-gold text-white rounded-2xl hover:shadow-lg active:scale-95 transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-bold font-montserrat text-lg">Enviar email</p>
                      <p className="text-sm text-white/90 font-montserrat">Contacta por correo electrónico</p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-white/80" />
                  </button>
                )}
              </div>

              {/* Info Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-6">
                <div className="flex gap-3">
                  <StoreIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900 font-montserrat mb-2">
                      Sobre el vendedor
                    </p>
                    <p className="text-sm text-blue-800 font-montserrat">
                      Esta es la tienda que envió tu pedido. Si tienes preguntas sobre el producto,
                      cambios, devoluciones o cualquier consulta relacionada con tu compra,
                      puedes contactarlos directamente usando los métodos arriba.
                    </p>
                  </div>
                </div>
              </div>

              {/* ── ADS: After store info + contact section ── */}
              {/* Great context: user just looked at the store, natural transition to "more from this store" */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <RecommendedProductsRow
                  deliveryId={delivery.id}
                  placement="store_info"
                />
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

export default StoreInfo;